import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  GetSingleChart,
  Option_Detail,
  ScalpingPositionDetails,
} from "../../CommonAPI/User";
import { Get_Pattern_Charting, Get_Pattern_Name } from "../../CommonAPI/Admin";
import Content from "../../../ExtraComponent/Content";
import { LibraryIcon,User,Users } from "lucide-react";

// Strategy options organized by category
const STRATEGY_OPTIONS = {
  "Straddle/Strangle": [
    { title: "Long Strangle", value: "LongStrangle" },
    { title: "Short Strangle", value: "ShortStrangle" },
    { title: "Long Straddle", value: "LongStraddle" },
    { title: "Short Straddle", value: "ShortStraddle" },
  ],
  "Butterfly/Condor": [
    { title: "Long Iron Butterfly", value: "LongIronButterfly" },
    { title: "Short Iron Butterfly", value: "ShortIronButterfly" },
    { title: "Long Iron Condor", value: "LongIronCondor" },
    { title: "Short Iron Condor", value: "ShortIronCondor" },
  ],
  Spread: [
    { title: "Bear Call Spread", value: "BearCallSpread" },
    { title: "Bear Put Spread", value: "BearPutSpread" },
    { title: "Bull Call Spread", value: "BullCallSpread" },
    { title: "Bull Put Spread", value: "BullPutSpread" },
  ],
  "Ladder/Covered": [
    { title: "Bull Call Ladder", value: "BullCallLadder" },
    { title: "Bull Put Ladder", value: "BullPutLadder" },
    { title: "Covered Call", value: "CoveredCall" },
    { title: "Covered Put", value: "CoveredPut" },
  ],
  "Collar/Ratio": [
    { title: "Long Collar", value: "LongCollar" },
    { title: "Short Collar", value: "ShortCollar" },
    { title: "Ratio Call Spread", value: "RatioCallSpread" },
    { title: "Ratio Put Spread", value: "RatioPutSpread" },
  ],
};

// Tabs available in the component
const TABS = ["Scalping", "Option", "Pattern"];

const DescriptionPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState("Scalping");

  // Scalping tab state
  const [scalpingOption, setScalpingOption] = useState("Single");

  // Option tab state
  const [measurementType, setMeasurementType] = useState("Straddle/Strangle");
  const [selectedOption, setSelectedOption] = useState("");

  // Pattern tab state
  const [selectedPatternType, setSelectedPatternType] =
    useState("Charting Pattern");
  const [selectedPatternName, setSelectedPatternName] = useState("");
  const [patternTypeOptions, setPatternTypeOptions] = useState([]);

  // Common state
  const [description, setDescription] = useState([]);
  const [patternData, setPatternData] = useState([]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);

    // Initialize default settings for each tab
    if (tab === "Scalping") {
      setScalpingOption("Single");
      fetchScalpingData("Single");
    } else if (tab === "Option") {
      const defaultStrategyCategory = STRATEGY_OPTIONS[measurementType];
      if (defaultStrategyCategory && defaultStrategyCategory.length > 0) {
        setSelectedOption(defaultStrategyCategory[0].value);
        fetchOptionData(defaultStrategyCategory[0]);
      }
    } else if (tab === "Pattern") {
      setSelectedPatternType("Charting Pattern");
      fetchPatternTypeOptions("Charting Pattern");
    }
  };

  // Fetch scalping data
  const fetchScalpingData = async (option) => {
    try {
      const reqData = { PositionType: option };
      const res = await ScalpingPositionDetails(reqData);
      setDescription(res.data);
    } catch (error) {
      console.error("Error fetching scalping data:", error);
    }
  };

  // Fetch option data
  const fetchOptionData = async (option) => {
    try {
      const reqData = { StrategyName: option.value };
      const res = await Option_Detail(reqData);
      setDescription(res.data);
    } catch (error) {
      console.error("Error fetching option data:", error);
    }
  };

  // Fetch pattern data
  const fetchPatternData = async () => {
    if (!selectedPatternName) return;

    try {
      const reqData = {
        Pattern: selectedPatternName,
        PatternType: selectedPatternType,
        TType: "",
      };
      const res = await GetSingleChart(reqData);
      setPatternData(res?.data || []);
    } catch (error) {
      console.error("Error fetching pattern data:", error);
    }
  };

  // Fetch pattern type options
  const fetchPatternTypeOptions = async (patternType) => {
    try {
      const patternTypeToUse = patternType || selectedPatternType;
      let data;

      if (patternTypeToUse === "Charting Pattern") {
        data = await Get_Pattern_Charting();
      } else {
        data = await Get_Pattern_Name();
      }

      setPatternTypeOptions(data.PatternName || []);

      // Select first pattern by default if available
      if (data.PatternName && data.PatternName.length > 0) {
        setSelectedPatternName(data.PatternName[0]);
      }
    } catch (error) {
      console.error("Error fetching pattern type options:", error);
    }
  };

  // Event handlers
  const handleScalpingChange = (option) => {
    setScalpingOption(option);
    fetchScalpingData(option);
  };

  const handleOptionChange = (option) => {
    setSelectedOption(option.value);
    fetchOptionData(option);
  };

  const handlePatternTypeChange = (e) => {
    const value = e.target.value;
    setSelectedPatternType(value);
    fetchPatternTypeOptions(value);
  };

  const handlePatternNameChange = (e) => {
    const value = e.target.value;
    setSelectedPatternName(value);
  };

  // Initialize component on mount
  useEffect(() => {
    handleTabChange(activeTab);
  }, []);

  // Update pattern data when pattern selection changes
  useEffect(() => {
    fetchPatternData();
  }, [selectedPatternName, selectedPatternType]);

  // Update option data when measurement type changes
  useEffect(() => {
    if (activeTab === "Option" && measurementType) {
      const defaultStrategy = STRATEGY_OPTIONS[measurementType][0];
      setSelectedOption(defaultStrategy.value);
      fetchOptionData(defaultStrategy);
    }
  }, [measurementType, activeTab]);

  return (
    <Content
      Page_title={"📄 Description"}
      button_status={false}
      backbutton_status={true}
    >
        <nav aria-label="breadcrumb" className="mb-3">
              <ol className="breadcrumb">
                <li className="breadcrumb-item active " aria-current="page">
                  Description
                </li>
              </ol>
            </nav>
            
      <div className="desc-page-wrapper ">
        <div className=" ">
          {/* Tab Navigation */}
          <ul className="nav nav-tabs rounded-0 w-100  mb-3">
            {TABS.map((tab) => (
              <li className="nav-item" key={tab}>
                <a
                 
                  className={`nav-link ${
                    activeTab === tab ? "active" : ""
                  }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-3">
            {/* Scalping Tab Content */}
            {activeTab === "Scalping" && (
              <div className="scalping-content">
            
             <ul className="desc-btn-group nav nav-pills l p-1 d-flex flex-wrap mb-0 justify-content-start">
  {[
    { label: "Single", icon: <User size={16} className="me-1" /> },
    { label: "Multiple", icon: <Users size={16} className="me-1" /> },
  ].map((option) => (
    <li className="nav-item me-2" key={option.label}>
      <button
        className={`nav-link ${scalpingOption === option.label ? "active" : ""}`}
        onClick={() => handleScalpingChange(option.label)}
      >
        {option.icon}
        {option.label}
      </button>
    </li>
  ))}
</ul>
                <div className="desc-details mt-3">
                  <div className="desc-card ">
                    <p className="desc-text">
                      <strong className="card-text-Color">
                        Position Type:
                      </strong>{" "}
                      <span >
                        {description?.[0]?.PositionType}
                      </span>
                    </p>
                    <p className="desc-text">
                  
                      <strong className="card-text-Color">
                        Description:
                      </strong>{" "}
                      <div >
                        {description?.[0]?.Description?.split("\n").map(
                          (line, index) => {
                            // Headings detection (like "Example -" or "A. Fixed Target:")
                            const isHeading =
                              line.trim().endsWith(":") ||
                              line.trim().toLowerCase().startsWith("example");

                            return (
                              <React.Fragment key={index}>
                                {isHeading ? (
                                  <h6 className="mt-3 mb-1 text-secondary fw-bold">
                                    {line}
                                  </h6>
                                ) : line.trim().startsWith("•") ||
                                  line.trim().match(/^[A-D]\./) ? (
                                  <li className="ms-3">{line}</li>
                                ) : (
                                  <p className="mb-1">{line}</p>
                                )}
                              </React.Fragment>
                            );
                          }
                        )}
                      </div>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Option Tab Content */}
            {activeTab === "Option" && (
              <div>
                <div className="dropdown-container mb-3 card-bg-color pb-3">
                  <label htmlFor="measurementType" className="form-label">
                    Select Option Type
                  </label>
                  <select
                    id="measurementType"
                    className="form-select"
                    value={measurementType}
                    onChange={(e) => setMeasurementType(e.target.value)}
                  >
                    {Object.keys(STRATEGY_OPTIONS).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {measurementType && (
                  <>
                    <h5 className="">Select a Strategy</h5>
                    <div className="desc-btn-group nav nav-pills responsive-nav justify-content-start ">
                      {STRATEGY_OPTIONS[measurementType].map((opt) => (
                        <li className="nav-item me-2" key={opt.value}>
                        <button
                          key={opt.value}
                          className={`nav-link ${
                            selectedOption === opt.value ? "active" : ""
                          }`}
                          onClick={() => handleOptionChange(opt)}
                        >
                          {opt.title}
                          </button>
                          </li>
                        
                      ))}
                    </div>
                  </>
                )}

                <div className="desc-details mt-3">
                  {description && description.length > 0 ? (
                    <div className="option-details d-flex flex-wrap card-bg-color">
                      <div className="option-image-container">
                        {description[0].image_data && (
                          <img
                            src={`data:image/png;base64,${description[0].image_data}`}
                            alt="Strategy"
                            className="option-image"
                          />
                        )}
                      </div>
                      <div className="option-info">
  <ul className="list-group">
    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Strategy Name:</strong>
      <span>{description[0]["Strategy Name"]}</span>
    </li>

    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Market Outlook:</strong>
      <span>{description[0]["View (Market Outlook)"]}</span>
    </li>

    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Strategy:</strong>
      <span>{description[0]["Strategy"]}</span>
    </li>

    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Risk (Max Loss):</strong>
      <span>{description[0]["Risk (Max Loss)"]}</span>
    </li>

    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Reward (Max Profit):</strong>
      <span>{description[0]["Reward (Max Profit)"]}</span>
    </li>

    <li className="list-group-item">
      <strong className="text-dark d-block mb-2">Breakeven Points:</strong>
      <ul className="list-group list-group-flush ms-3">
        <li className="list-group-item d-flex justify-content-between align-items-center">
          <strong>Upper BE:</strong>
          <span>{description[0]["Breakeven Points"]?.["Upper BE"]}</span>
        </li>
        <li className="list-group-item d-flex justify-content-between align-items-center">
          <strong>Lower BE:</strong>
          <span>{description[0]["Breakeven Points"]?.["Lower BE"]}</span>
        </li>
      </ul>
    </li>

    <li className="list-group-item">
      <strong className="text-dark d-block mb-2">Max Profit When:</strong>
      <ul className="list-group list-group-flush ms-3">
        <li className="list-group-item d-flex justify-content-between align-items-center">
          <strong>Upward:</strong>
          <span>{description[0]["Max Profit When?"]?.["Upward"]}</span>
        </li>
        <li className="list-group-item d-flex justify-content-between align-items-center">
          <strong>Downward:</strong>
          <span>{description[0]["Max Profit When?"]?.["Downward"]}</span>
        </li>
      </ul>
    </li>

    <li className="list-group-item d-flex justify-content-between align-items-center">
      <strong className="text-dark">Max Loss When:</strong>
      <span>{description[0]["Max Loss When?"]}</span>
    </li>
  </ul>
</div>

                    </div>
                  ) : (
                    <p className="card-text-Color">
                      No option details available.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Pattern Tab Content */}
            {activeTab === "Pattern" && (
              <div>
                <div className="dropdown-container mb-3 card-bg-color pb-3 justify-content-start">
                {/* <h5 className="desc-heading">Select a Pattern</h5> */}
                <div className="desc-btn-group d-flex">
                  <div className="dropdown-container me-2 w-50">
                    <label htmlFor="patternType" className="form-label">
                      Select Pattern Type
                    </label>
                    <select
                      id="patternType"
                      className="form-select"
                      value={selectedPatternType}
                      onChange={handlePatternTypeChange}
                    >
                      <option value="Charting Pattern">Charting Pattern</option>
                      <option value="CandleStick Pattern">
                        CandleStick Pattern
                      </option>
                    </select>
                  </div>
                  <div className="dropdown-container w-50">
                    <label htmlFor="patternName" className="form-label">
                      Select Pattern Name
                    </label>
                    <select
                      id="patternName"
                      className="form-select"
                      value={selectedPatternName}
                      onChange={handlePatternNameChange}
                    >
                      {patternTypeOptions.map((pattern) => (
                        <option key={pattern} value={pattern}>
                          {pattern}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                </div>
                <div className="desc-details mt-3">
                  <div className=" d-flex card-bg-color">
                    <div className="image-container-unique">
                      {patternData?.[0]?.image_data && (
                        <img
                          src={`data:image/png;base64,${patternData[0].image_data}`}
                          alt={patternData[0].Pattern}
                          className="pattern-image-unique"
                        />
                      )}
                    </div>
                    <div className="text-content-unique">
                      <h2 className="pattern-title-unique">
                        {patternData?.[0]?.Pattern}
                      </h2>
                      <p className="pattern-type-unique">
                        <strong className="card-text-Color">Type:</strong>{" "}
                        {patternData?.[0]?.PatternType}
                      </p>
                      <p className="trading-type-unique">
                        <strong className="card-text-Color">
                          Trading Type:
                        </strong>{" "}
                        {patternData?.[0]?.TType}
                      </p>
                      <p className="pattern-description-unique card-bg-color">
                        {patternData?.[0]?.Description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Content>
  );
};

export default DescriptionPage;
